import * as React from "react";
import { useQuery } from "@tanstack/react-query";

import { CoachSettings } from "@/interfaces";
import { Skeleton } from "@/components/ui";

import { QuickActionCard } from "./components/QuickActionCard";
import { VideoPageAssignmentsQuickActionCard } from "./components/VideoPageAssignmentsQuickActionCard";
import { SharePlayerCardDialog } from "./components/SharePlayerCardDialog";
import { ShareFormLinkDialog } from "@/components/ShareFormLinkDialog";
import { usePlayerSettings, useSuperPageAssignmentsOverview } from "@/utils";
import { PlayerDashboardServices } from "@/services/bedrock";

interface OwnProps {
  playerId: number;
}
type Props = OwnProps;

const QuickActionsFC: React.FC<Props> = ({ playerId }) => {
  const [showShareDialog, setShowShareDialog] = React.useState<boolean>(false);
  const [showReviewLinkDialog, setShowReviewLinkDialog] = React.useState<boolean>(false);

  const { hasSetting } = usePlayerSettings(playerId);
  const hasMVP = React.useMemo(() => hasSetting(CoachSettings.PLAYER_MVP_ACCESS), [hasSetting]) === true;

  const { superPageAssignmentsOverview: assignmentsOverview, isFetching: isFetchingAssignmentsOverview } =
    useSuperPageAssignmentsOverview({ enabled: hasMVP });

  const completedAssignmentsCount = assignmentsOverview?.totalCompleted ?? 0;
  const requestedAssignmentsCount = assignmentsOverview?.totalRequested ?? 0;
  const showVideoPageAssignmentsCard =
    hasMVP && !isFetchingAssignmentsOverview && !!assignmentsOverview && requestedAssignmentsCount > 0;

  // Fetch Quick Actions
  const { data: actions = [], isLoading } = useQuery({
    queryKey: ["playerQuickActions", playerId],
    queryFn: async () => {
      const allActions = await PlayerDashboardServices.getQuickActions();
      return allActions.filter(
        a =>
          a.actionTarget === "CUSTOMIZATIONS" ||
          a.actionTarget === "SOCIAL_NETWORKS" ||
          a.actionTarget === "PROMPT_LIBRARY" ||
          a.actionTarget === "SCRIPT_LIBRARY" ||
          a.actionTarget === "PROJECT_LIBRARY" ||
          a.actionTarget === "NEW_VIDEO_PAGE" ||
          a.actionTarget === "NEW_VIDEO_PROJECT" ||
          a.actionTarget === "SHARE_PLAYER_CARD" ||
          a.actionTarget === "SHARE_REVIEW_LINK" ||
          a.actionTarget === "UNREPLIED_REVIEWS"
      );
    },
    enabled: !!playerId,
  });

  return (
    <div className="statisticsContent w-full">
      <div className="w-full flex flex-col gap-2">
        {isLoading ? (
          <div className="statistics flex flex-col gap-2">
            <div className="text-2xl font-bold text-slate-700 leading-snug">Get Started</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 w-full">
              {Array.from({ length: 8 }).map((_, idx) => (
                <div
                  key={`skeleton-${idx}`}
                  style={{ height: "120px" }}
                  className="w-full bg-white p-4 rounded-xl border border-slate-200/50 flex flex-col gap-2.5 animate-pulse select-none"
                >
                  <Skeleton className="h-5 w-36" />
                  <Skeleton className="h-4 w-48 mt-1" />
                  <Skeleton className="h-3.5 w-40" />
                </div>
              ))}
            </div>
          </div>
        ) : actions.length > 0 || showVideoPageAssignmentsCard ? (
          <div className="statistics flex flex-col gap-4">
            <div>
              <div className="text-2xl font-bold text-slate-700 leading-snug">Get Started</div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 w-full">
              {showVideoPageAssignmentsCard && (
                <VideoPageAssignmentsQuickActionCard
                  key="video-page-assignments"
                  completedCount={completedAssignmentsCount}
                  requestedCount={requestedAssignmentsCount}
                />
              )}
              {actions.map((action, index) => (
                <QuickActionCard
                  key={index}
                  action={action}
                  onSharePlayerCardClick={() => setShowShareDialog(true)}
                  onShareReviewLinkClick={() => setShowReviewLinkDialog(true)}
                />
              ))}
            </div>
          </div>
        ) : null}
      </div>

      {/* Share Player Card Dialog */}
      <SharePlayerCardDialog open={showShareDialog} onOpenChange={setShowShareDialog} playerId={playerId} />

      {/* Share Review Link Dialog */}
      <ShareFormLinkDialog open={showReviewLinkDialog} onOpenChange={setShowReviewLinkDialog} playerId={playerId} />
    </div>
  );
};

export const QuickActions = QuickActionsFC;
